import { useState } from 'react';
import { Card } from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Calendar } from "@shared/components/ui/calendar";
import { Key, MapPin, Clock, CheckCircle2, Loader2, PartyPopper } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, isWeekend } from 'date-fns';
import { ar } from 'date-fns/locale';
import { getPhotoUrl } from '@/types';

const timeSlots = [
  '09:00 - 10:00',
  '10:00 - 11:00',
  '11:00 - 12:00',
  '14:00 - 15:00',
  '15:00 - 16:00',
  '16:00 - 17:00'
];

export default function KeyPickup({ property, onComplete }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const formatDate = (date) => {
    return format(date, 'EEEE، dd MMMM yyyy', { locale: ar });
  };

  const handleConfirm = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsComplete(true);
      setIsLoading(false);
    }, 2000);
  };

  const disabledDays = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today || isWeekend(date);
  };

  if (isComplete) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-emerald-50 to-teal-50"
      >
        <Card className="w-full max-w-lg p-8 border-0 shadow-2xl text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.5 }}
          >
            <div className="w-24 h-24 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/30">
              <PartyPopper className="w-12 h-12 text-white" />
            </div>
          </motion.div>

          <h1 className="text-3xl font-bold text-slate-900 mb-4">مبروك!</h1>
          <p className="text-lg text-slate-600 mb-8">
            تم تأكيد موعد استلام المفتاح بنجاح
          </p>

          <div className="bg-slate-50 rounded-xl p-6 mb-8 text-right">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">التاريخ</p>
                  <p className="font-semibold text-slate-900">{formatDate(selectedDate)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">الوقت</p>
                  <p className="font-semibold text-slate-900">{selectedTime}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">الموقع</p>
                  <p className="font-semibold text-slate-900">{property?.address}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              onClick={() => onComplete()}
              className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-lg font-medium"
            >
              العودة للرئيسية
            </Button>
          </div>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen p-4 md:p-8 bg-gradient-to-br from-slate-50 to-slate-100"
    >
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Key className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">حدد موعد استلام المفتاح</h1>
          <p className="text-slate-500">اختر الوقت المناسب لاستلام مفتاح منزلك الجديد</p>
        </div>

        <Card className="p-6 md:p-8 border-0 shadow-xl mb-6">
          {/* Property Info */}
          <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl mb-6">
            <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
              <img
                src={getPhotoUrl(property?.photos, 'thumb')}
                alt={property?.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">{property?.title}</h3>
              <div className="flex items-center gap-1.5 text-slate-500">
                <MapPin className="w-4 h-4" />
                <span className="text-sm">
                  {property?.district?.label ? `${property.district.label}، ` : ''}
                  {property?.city?.label}
                </span>
              </div>
            </div>
          </div>

          {/* Calendar */}
          <div className="mb-6">
            <h3 className="font-semibold text-slate-900 mb-4">اختر التاريخ</h3>
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={disabledDays}
                locale={ar}
                className="rounded-xl border border-slate-200"
              />
            </div>
          </div>

          {/* Time Slots */}
          <AnimatePresence>
            {selectedDate && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <h3 className="font-semibold text-slate-900 mb-4">اختر الوقت</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                  {timeSlots.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => setSelectedTime(slot)}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        selectedTime === slot
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Clock className="w-4 h-4 mx-auto mb-1" />
                      <span className="text-sm font-medium">{slot}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <Button
            onClick={handleConfirm}
            disabled={!selectedDate || !selectedTime || isLoading}
            className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-lg font-medium disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'تأكيد الموعد'
            )}
          </Button>
        </Card>
      </div>
    </motion.div>
  );
}